import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import { MetricGridSkeleton } from "@/components/Skeleton";
import { toFormat } from "@/lib/dateHelpers";
import type { Path } from "@/router";

export type GrpPersonBenefitsStatus = "loading" | "error" | "forbidden" | "not-enrolled" | "enrolled";

export type GrpPersonBenefitsViewProps = {
  status: GrpPersonBenefitsStatus;
  accountOpenedOn?: string | null;
  expiredOn?: string | null;
  errorMessage?: string;
  onRetry?: () => void;
};

const GRP_ENROLLMENT_ICON = "volunteer_activism" as const;

/** Storybook `expanded` is 4-across (`md:grid-cols-4`); a single card is too narrow for "Enrollment Date: …". `thirds` is the documented 3-across layout with no `minCardWidth`. */
const GRP_ENROLLMENT_GRID_LAYOUT = "thirds" as const;

function formatStatusDate(date?: string | null): string {
  if (!date) return "—";
  return toFormat(date) || date || "—";
}

function enrollmentMetrics(args: { enrolled: boolean; date?: string | null }): MetricCardType<Path>[] {
  const { enrolled, date } = args;
  const formattedDate = formatStatusDate(date);

  return [
    {
      value: enrolled ? "Enrolled" : "Not Enrolled",
      variant: enrolled ? "success" : "error",
      iconName: GRP_ENROLLMENT_ICON,
      label: enrolled ? `Enrollment Date: ${formattedDate}` : `Expired Date: ${formattedDate}`,
      testId: "grp-person-benefits-card"
    }
  ];
}

export default function GrpPersonBenefitsView({
  status,
  accountOpenedOn,
  expiredOn,
  errorMessage,
  onRetry
}: GrpPersonBenefitsViewProps) {
  if (status === "loading") {
    return <MetricGridSkeleton cards={1} layout={GRP_ENROLLMENT_GRID_LAYOUT} />;
  }

  if (status === "error") {
    return (
      <div className="pt-12">
        <ErrorBoundary
          variant="inline"
          title="Unable to load Golf Retirement Plus"
          message={errorMessage}
          action={
            onRetry ? (
              <Button variant="outlined" onClick={onRetry} testId="grp-person-benefits-retry">
                Retry
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <EmptyState
        iconName="lock"
        title="You Don't Have Access to This Page"
        caption="Viewing Golf Retirement Plus enrollments requires the view GRP enrollments permission. Contact your administrator if you need access."
      />
    );
  }

  if (status === "not-enrolled") {
    return (
      <MetricGrid
        metrics={enrollmentMetrics({ enrolled: false, date: expiredOn })}
        layout={GRP_ENROLLMENT_GRID_LAYOUT}
      />
    );
  }

  return (
    <MetricGrid
      metrics={enrollmentMetrics({ enrolled: true, date: accountOpenedOn })}
      layout={GRP_ENROLLMENT_GRID_LAYOUT}
    />
  );
}
